import React, { useState, useEffect, useRef } from 'react';
import { Map, Marker, Popup, Source, Layer } from 'react-map-gl';
import mapboxgl from 'mapbox-gl';
import { Player } from '@lottiefiles/react-lottie-player';
import { createRoot } from 'react-dom/client';
import { app } from '../firebaseConfig';
import { httpsCallable, getFunctions } from 'firebase/functions';
import '../Map.css';
import animation1 from '../lotties/lottie_walkingcoffee.json';

// Custom Lottie marker for user location
const LottieMarker = () => (
  <div style={{ width: '50px', height: '50px' }}>
    <Player src={animation1} loop autoplay style={{ width: '50px', height: '50px' }} />
  </div>
);

// Mapbox layer styles for cafes (individual and clustered)
const cafeLayer = {
  id: 'cafes',
  type: 'circle',
  source: 'cafes',
  filter: ['!', ['has', 'point_count']],
  paint: {
    'circle-radius': 12,
    'circle-color': '#f48e28',
    'circle-stroke-width': 2,
    'circle-stroke-color': '#CC7722',
  },
};

const clusterLayer = {
  id: 'cafe-clusters',
  type: 'circle',
  source: 'cafes',
  filter: ['has', 'point_count'],
  paint: {
    'circle-radius': ['step', ['get', 'point_count'], 20, 100, 30, 750, 40],
    'circle-color': '#f48e28',
    'circle-opacity': 0.8,
  },
};

const clusterCountLayer = {
  id: 'cafe-cluster-count',
  type: 'symbol',
  source: 'cafes',
  filter: ['has', 'point_count'],
  layout: {
    'text-field': '{point_count_abbreviated}',
    'text-font': ['Roboto Bold'],
    'text-size': 12,
  },
  paint: {
    'text-color': '#ffffff',
  },
};

function CafeMap() {
  const [allCafes, setAllCafes] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCafe, setSelectedCafe] = useState(null);
  const [viewState, setViewState] = useState({
    latitude: 49.2827,
    longitude: -123.1207,
    zoom: 1,
  });
  const [isInteractive, setIsInteractive] = useState(false);
  const [userLocation, setUserLocation] = useState({ latitude: null, longitude: null }); // New state
  const mapRef = useRef(null);
  const userMarkerRef = useRef(null);
  const watchIdRef = useRef(null);
  const hasCenteredMapRef = useRef(false);
  const [mapLoaded, setMapLoaded] = useState(false);

  // Calculate distance between two points
  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    if (!lat1 || !lng1 || !lat2 || !lng2) {
      console.warn('Bad coords:', { lat1, lng1, lat2, lng2 });
      return 'Unknown';
    }
    const R = 6371e3;
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lng2 - lng1) * Math.PI) / 180;
    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distanceMeters = R * c;
    return distanceMeters < 1000
      ? `${Math.round(distanceMeters)} meters`
      : `${(distanceMeters / 1000).toFixed(1)} km`;
  };

  // Fetch cafe locations from Firebase
  const fetchAllCafes = async () => {
    try {
      setError(null);
      const functions = getFunctions(app);
      const getAllCafeLocations = httpsCallable(functions, 'getAllCafeLocations');
      const result = await getAllCafeLocations();
      const cafeData = result.data;

      

      if (!Array.isArray(cafeData)) {
        throw new Error('Cafe locations data is not an array');
      }

      if (cafeData.length === 0) {
        setError('No cafes found');
        setAllCafes([]);
        return;
      }

      const parsedCafes = cafeData
        .map(cafe => {
          const latitude = parseFloat(cafe.latitude);
          const longitude = parseFloat(cafe.longitude);
          if (isNaN(latitude) || isNaN(longitude)) {
            console.warn('Skipping cafe with invalid coordinates:', cafe);
            return null;
          }
          return {
            ...cafe,
            latitude,
            longitude,
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [longitude, latitude],
            },
            properties: { name: cafe.name, address: cafe.address, rating: cafe.rating },
          };
        })
        .filter(cafe => cafe !== null);

      if (parsedCafes.length === 0) {
        setError('No valid cafes found after parsing');
        setAllCafes([]);
        return;
      }

      setAllCafes(parsedCafes);
    } catch (error) {
      console.error('Error fetching cafes:', error);
      setError(`Failed to load cafes: ${error.message}`);
      setAllCafes([]);
    }
  };

  // Update user marker with Lottie
  const updateUserMarker = (lat, lng) => {
    if (!mapLoaded || !mapRef.current) {
      console.warn('Map not loaded, skipping updateUserMarker');
      return;
    }
    setUserLocation({ latitude: lat, longitude: lng }); // Update state
    if (userMarkerRef.current) {
      userMarkerRef.current.remove();
    }
    const el = document.createElement('div');
    el.style.width = '50px';
    el.style.height = '50px';
    const root = createRoot(el);
    root.render(<LottieMarker />);
    userMarkerRef.current = new mapboxgl.Marker({ element: el })
      .setLngLat([lng, lat])
      .setPopup(new mapboxgl.Popup().setHTML('<h3>You are here</h3>'))
      .addTo(mapRef.current.getMap());
  };

  // Handle cluster click to zoom
  const handleClusterClick = (e) => {
    if (!mapRef.current) return;
    const features = mapRef.current.queryRenderedFeatures(e.point, { layers: ['cafe-clusters'] });
    if (!features.length) return;
    const clusterId = features[0].properties.cluster_id;
    mapRef.current.getSource('cafes').getClusterExpansionZoom(clusterId, (err, zoom) => {
      if (err) return;
      mapRef.current.easeTo({ center: features[0].geometry.coordinates, zoom: zoom + 1 });
    });
  };

  // Handle cafe click for popup
  const handleCafeClick = (e) => {
    if (!mapRef.current) return;
    const features = mapRef.current.queryRenderedFeatures(e.point, { layers: ['cafes'] });
    if (!features.length) return;
    const cafe = features[0].properties;
    const { coordinates } = features[0].geometry;
    const { latitude, longitude } = userLocation; // Use state
    const distance = latitude && longitude
      ? calculateDistance(latitude, longitude, coordinates[1], coordinates[0])
      : 'Unknown';
    setSelectedCafe({ ...cafe, coordinates, distance });
  };

  // Normalize longitude to [-180, 180]
  const normalizeLongitude = (lng) => {
    return ((lng + 180) % 360) - 180;
  };

  // Handle map load with spin and smooth zoom
  const handleMapLoad = () => {
    setMapLoaded(true);
    let totalDegreesSpun = 0;
    const spins = 1;
    const degreesPerSpin = 360;
    const totalDegreesToSpin = spins * degreesPerSpin;
    const spinSpeed = 2; // 1.5 degrees per frame
    const spinIntervalTime = 30; // 30ms per frame (~7.5s for 360 degrees)
    let targetLat = 49.2827; // Default fallback
    let targetLng = -123.1207;

    // Get user location early
    navigator.geolocation.getCurrentPosition(
      (position) => {
        targetLat = position.coords.latitude;
        targetLng = normalizeLongitude(position.coords.longitude);
        setUserLocation({ latitude: targetLat, longitude: targetLng }); // Set initial location
      },
      (error) => {
        console.error('Initial geolocation error:', error);
        targetLat = 49.2827;
        targetLng = -123.1207;
        setUserLocation({ latitude: targetLat, longitude: targetLng }); // Set fallback
      },
      { timeout: 5000, maximumAge: 0 }
    );

    const spinInterval = setInterval(() => {
      setViewState(prev => {
        totalDegreesSpun += spinSpeed;
        const newLongitude = prev.longitude + spinSpeed;

        if (totalDegreesSpun >= totalDegreesToSpin) {
          clearInterval(spinInterval);
          // Smooth zoom with lat/lng interpolation
          const startZoom = 1;
          const endZoom = 13;
          const zoomSteps = 90; // ~5s at 30ms per step
          const zoomIncrement = (endZoom - startZoom) / zoomSteps;
          const startLat = prev.latitude;
          const startLng = normalizeLongitude(newLongitude);
          const latIncrement = (targetLat - startLat) / zoomSteps;
          let deltaLng = targetLng - startLng;
          if (Math.abs(deltaLng) > 180) {
            deltaLng += deltaLng > 0 ? -360 : 360;
          }
          const lngIncrement = deltaLng / zoomSteps;
          let step = 0;

          const zoomInterval = setInterval(() => {
            step++;
            if (step >= zoomSteps) {
              clearInterval(zoomInterval);
              setViewState({ latitude: targetLat, longitude: targetLng, zoom: endZoom });
              updateUserMarker(targetLat, targetLng);
              hasCenteredMapRef.current = true;
              setIsInteractive(true); // Enable full interactions
              // Explicitly enable Mapbox interactions
              const map = mapRef.current.getMap();
              map.dragPan.enable();
              map.scrollZoom.enable();
              map.doubleClickZoom.enable();
              map.touchZoomRotate.enable();
              map.boxZoom.enable();
              map.keyboard.enable();
              watchIdRef.current = navigator.geolocation.watchPosition(
                (pos) => {
                  const { latitude, longitude } = pos.coords;
                  updateUserMarker(latitude, longitude);
                },
                (error) => console.error('Geolocation watch error:', error),
                { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
              );
              return;
            }
            setViewState({
              latitude: startLat + latIncrement * step,
              longitude: normalizeLongitude(startLng + lngIncrement * step),
              zoom: startZoom + zoomIncrement * step,
            });
          }, 30);
          return prev;
        }

        return { ...prev, longitude: normalizeLongitude(newLongitude) };
      });
    }, spinIntervalTime);
  };

  useEffect(() => {
    fetchAllCafes(); // Fetch cafes immediately
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => {
      clearTimeout(timer);
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      if (userMarkerRef.current) {
        userMarkerRef.current.remove();
      }
    };
  }, []);

  return (
    <div className="map-block">
      <div className={`map-container ${isVisible ? 'visible' : ''}`}>
        {error && <div className="error">{error}</div>}
        <Map
          ref={mapRef}
          {...viewState}
          onMove={(evt) => setViewState(evt.viewState)}
          style={{ width: '100%', height: '100%' }}
          mapStyle="mapbox://styles/mapbox/standard"          
          mapboxAccessToken="pk.eyJ1Ijoiam9zaGJlYW5ib251cyIsImEiOiJjbWJmZzRyaWUxdGlxMmlxMWltaXp4a2JmIn0.LLxXrjW-8UohLLmqDSNv6g"
          projection="globe"
          maxPitch={0}
          fog={{ range: [0.5, 10], color: 'rgba(186, 210, 235, 0.5)', 'horizon-blend': 0.1 }}
          interactive={isInteractive} // Enable interactions post-zoom
          onClick={(e) => {
            handleClusterClick(e);
            handleCafeClick(e);
          }}
          onLoad={handleMapLoad}
          maxTileCacheSize={100}
        >
          {allCafes.length > 0 && (
            <Source
              id="cafes"
              type="geojson"
              data={{
                type: 'FeatureCollection',
                features: allCafes,
              }}
              cluster
              clusterMaxZoom={13}
              clusterRadius={20}
              tolerance={0.5}
            >
              <Layer {...cafeLayer} />
              <Layer {...clusterLayer} />
              <Layer {...clusterCountLayer} />
            </Source>
          )}
          {selectedCafe && (
            <Popup
              longitude={selectedCafe.coordinates[0]}
              latitude={selectedCafe.coordinates[1]}
              onClose={() => setSelectedCafe(null)}
              closeOnClick={false}
              className="cafe-popup-wrapper"
            >
              <div className="cafe-popup">
                <h3 className="cafe-popup-title">{selectedCafe.name}</h3>
                <div className="cafe-popup-detail">
                  <span className="cafe-popup-label">Distance:</span>
                  <span className="cafe-popup-value">{selectedCafe.distance}</span>
                </div>
                {selectedCafe.address && (
                  <div className="cafe-popup-detail">
                    <span className="cafe-popup-label">Address:</span>
                    <span className="cafe-popup-value">{selectedCafe.address}</span>
                  </div>
                )}
                {selectedCafe.rating && (
                  <div className="cafe-popup-detail">
                    <span className="cafe-popup-label">Rating:</span>
                    <span className="cafe-popup-value">{selectedCafe.rating}/5</span>
                  </div>
                )}
              </div>
            </Popup>
          )}
        </Map>
      </div>
    </div>
  );
}

export default CafeMap;